import React, {
    useState, useEffect, useContext, useRef
} from 'react';
import { TrackingContext } from '@vrbo/react-event-tracking';
import throttle from 'lodash.throttle';
import Button, { LinkButtonType } from '@ferris/react/components/button/Button';
import PropTypes from 'prop-types';
import YouTube from 'react-youtube';
import cn from 'classnames';
import { createImpressionEvent, createInteractionEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import log from '../../utils/ClientLogger';


const MediaText = (props) => {
    const {
        headline,
        media,
        cta,
        copy,
        details,
        mediaType,
        arrangement,
        ctaType
    } = props;
    const [isMediaTextSectionViewed, setIsMediaTextSectionViewed] = useState(false);
    const { trigger } = useContext(TrackingContext);
    const MediaTextSection = useRef();

    const isMediaTextSectionVisible = () => {
        /* istanbul ignore else */
        if (MediaTextSection.current) {
            return (MediaTextSection.current.getBoundingClientRect().top
            - (MediaTextSection.current.offsetHeight + 150)) < 0;
        }
        return false;
    };

    const onScroll = () => {
        if (isMediaTextSectionVisible()) {
            setIsMediaTextSectionViewed(true);
        }
    };

    const scrollHandler = throttle(onScroll, 333);

    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        trigger('impression', createImpressionEvent('image_text_section.presented', 'Property Onboarding', details));
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);
    useEffect(() => {
        if (isMediaTextSectionViewed) {
            trigger('impression', createImpressionEvent('image_text_section.viewed', 'Property Onboarding', details));
        }
    }, [isMediaTextSectionViewed]);

    const onPlayHandler = () => {
        trigger('interaction', createInteractionEvent('activate_button.selected', 'Property Onboarding', details, '1.4.0'));
    };

    const onPlayErrorHandler = (e) => {
        log(['error'], 'Error playing video', e);
    };
    const clickHandler = () => {
        trigger('interaction', createInteractionEvent('action_copy_link.selected', 'Property Onboarding', details, `image_text_${details}`));
    };

    return (
        <section
            className={cn(
                'media-text-container',
                { 'media-text-container--left': arrangement === 'left' },
                { 'media-text-container--right': arrangement === 'right' }
            )}
            ref={MediaTextSection}
            id="Testimonials"
        >
            <div className={cn('media-section', { 'video-player': mediaType === 'video' })}>
                {
                    mediaType === 'image' && (
                        <img alt={media.alt ?? 'marketing image'} src={media.url} />
                    )
                }
                {
                    mediaType === 'video' && (
                        <YouTube
                            videoId={media.url}
                            onPlay={onPlayHandler}
                            onError={onPlayErrorHandler}
                            opts={{
                                width: '100%',
                                height: '100%'
                            }}
                        />
                    )
                }
            </div>
            <div className={cn('text-section',)}>
                <h2>{headline}</h2>
                <p>{copy}</p>
                {
                    cta.text && (
                        <div className={cn('text-cta')}>
                            <a onClick={clickHandler} href={cta.url} target={cta.target} rel="noreferrer">
                                {
                                    ctaType === 'link' && (<span>{cta.text}</span>)
                                }
                                {
                                    ctaType === 'button' && (
                                        <Button
                                            htmlButtonType="button"
                                            type="primary"
                                            onClick={clickHandler}
                                            variant={LinkButtonType.STANDARD}
                                            small
                                        >
                                            {cta.text}
                                        </Button>
                                    )
                                }
                            </a>
                        </div>
                    )
                }
            </div>
        </section>
    );
};

// eslint-disable-next-line camelcase
const MEDIA_TYPEs = ['image', 'video'];

const ARRANGEMENT = ['left', 'right'];

// eslint-disable-next-line camelcase
const CTA_TYPES = ['button', 'link'];

MediaText.defaultProps = {
    mediaType: 'image',
    arrangement: 'left',
    ctaType: 'button'
};

MediaText.propTypes = {
    headline: PropTypes.string.isRequired,
    media: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: PropTypes.string
    }).isRequired,
    cta: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired
    }).isRequired,
    copy: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    mediaType: PropTypes.oneOf(MEDIA_TYPEs),
    arrangement: PropTypes.oneOf(ARRANGEMENT),
    ctaType: PropTypes.oneOf(CTA_TYPES)
};

export default MediaText;
